import React, {useMemo, useState, useEffect, useCallback, useRef} from 'react';
import EditableTable from '../editableTable/editableTable';
import CustomModal from '../../../../../../../../components/custom-modal/custom-modal';
import {getCcfResult, FUNCTIONAL_SAFETY_CATEGORY} from '../../../../../../../../utils/constants';
import { Tabs, Tab, Box } from '@mui/material';

const CCF_TYPES = {
  INPUT: 'input',
  LOGIC: 'logic',
  OUTPUT: 'output',
  NOT_APPLICABLE: 'not_applicable',
};

const CCF = ({
  setShow,
  show,
  assessment,
  ccfOptions,
  ccfAssessments,
  createCCF,
  deleteCCF,
  upsertAssessment,
  isCreating,
  isDeleting,
}) => {
  const category = assessment?.category;

  // State for active tab
  const [activeTab, setActiveTab] = useState(0);

  // Determine which sections are required based on category
  const sectionRequirements = useMemo(() => {
    const requirements = {
      input: { required: false, show: true },
      logic: { required: true, show: true },
      output: { required: true, show: true },
    };

    // Category 3 and 4: All three sections required
    if (category === FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_3 || category === FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_4) {
      requirements.input.required = true;
    }
    // Category 2 and 2M: Input may not be required, Logic and Output required
    else if (category === FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_2 || category === FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_2M) {
      requirements.input.required = false;
    }

    return requirements;
  }, [category]);

  // Track "Not Applicable" state per type (local state to handle user interactions)
  const [notApplicableState, setNotApplicableState] = useState({
    input: false,
    logic: false,
    output: false,
  });

  // Track user-initiated "Not Applicable" selections to prevent useEffect from overriding
  const [userSetNotApplicable, setUserSetNotApplicable] = useState({
    input: false,
    logic: false,
    output: false,
  });

  // Track when user explicitly unchecks "Not Applicable" to prevent auto-checking
  const [userUncheckedNotApplicable, setUserUncheckedNotApplicable] = useState({
    input: false,
    logic: false,
    output: false,
  });

  const pendingDeletionsRef = useRef(new Set());
  const prevShowRef = useRef(false);

  const [deletingAssessmentIds, setDeletingAssessmentIds] = useState(new Set());

  const filteredAssessments = useMemo(() => {
    return ccfAssessments.filter(assessment => !deletingAssessmentIds.has(assessment.id));
  }, [ccfAssessments, deletingAssessmentIds]);

  useEffect(() => {
    setDeletingAssessmentIds(prev => {
      const newSet = new Set(prev);
      let hasChanges = false;
      
      prev.forEach(id => {
        const stillExists = ccfAssessments.some(a => a.id === id);
        if (!stillExists) {
          newSet.delete(id);
          pendingDeletionsRef.current.delete(id);
          hasChanges = true;
        }
      });
      
      if (show) {
        pendingDeletionsRef.current.forEach(id => {
          if (ccfAssessments.some(a => a.id === id) && !newSet.has(id)) {
            newSet.add(id);
            hasChanges = true;
          } else if (!ccfAssessments.some(a => a.id === id)) {
            pendingDeletionsRef.current.delete(id);
          }
        });
      }
      
      return hasChanges ? newSet : prev;
    });
  }, [show, ccfAssessments]);




  const shouldBeNotApplicable = useCallback((ccfType) => {
    const hasAssessments = filteredAssessments.some(a => a.ccf_type === ccfType);
    const isRequired = sectionRequirements[ccfType]?.required || false;
    return !hasAssessments && !isRequired;
  }, [filteredAssessments, sectionRequirements]);


  // When modal opens, sync from DB and reset user-intent state so reload/open shows persisted values
  useEffect(() => {
    const didOpen = show && !prevShowRef.current;
    prevShowRef.current = show;

    if (show) {
      pendingDeletionsRef.current.forEach(id => {
        if (ccfAssessments.some(a => a.id === id)) {
          setDeletingAssessmentIds(prev => {
            if (!prev.has(id)) {
              const newSet = new Set(prev);
              newSet.add(id);
              return newSet;
            }
            return prev;
          });
        } else {
          pendingDeletionsRef.current.delete(id);
        }
      });

      // Reset user-intent flags only when modal just opened so checkbox state comes from assessment
      if (didOpen) {
        setUserSetNotApplicable({ input: false, logic: false, output: false });
        setUserUncheckedNotApplicable({ input: false, logic: false, output: false });
      }
    }

    const fromDb = {
      input: Boolean(assessment?.ccf_input_not_applicable),
      logic: Boolean(assessment?.ccf_logic_not_applicable),
      output: Boolean(assessment?.ccf_output_not_applicable),
    };
    // When assessment exists, always use DB values (including all false). Otherwise derive from requirements.
    const hasAssessment = assessment != null;
    const initialState = hasAssessment
      ? fromDb
      : {
          input: shouldBeNotApplicable(CCF_TYPES.INPUT),
          logic: shouldBeNotApplicable(CCF_TYPES.LOGIC),
          output: shouldBeNotApplicable(CCF_TYPES.OUTPUT),
        };
    setNotApplicableState(initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, assessment?.ccf_input_not_applicable, assessment?.ccf_logic_not_applicable, assessment?.ccf_output_not_applicable]); 


  useEffect(() => {
    setNotApplicableState(prev => {
      const newState = { ...prev };
      let hasChanges = false;

      Object.keys(newState).forEach(type => {

        if (userSetNotApplicable[type]) {
          if (newState[type] !== true) {
            newState[type] = true;
            hasChanges = true;
          }
        }

        else if (!userSetNotApplicable[type]) {

          if (userUncheckedNotApplicable[type]) {
            if (newState[type] !== false) {
              newState[type] = false;
              hasChanges = true;
            }
          } else {
            const persistedNA = Boolean(assessment?.[`ccf_${type}_not_applicable`]);

            const shouldBeNA = assessment != null ? persistedNA : (persistedNA || shouldBeNotApplicable(type));
            if (newState[type] !== shouldBeNA) {
              newState[type] = shouldBeNA;
              hasChanges = true;
            }
          }
        }
      });

      return hasChanges ? newState : prev;
    });
  }, [assessment, filteredAssessments, sectionRequirements, userSetNotApplicable, userUncheckedNotApplicable, shouldBeNotApplicable]);


  const isOptionSelected = (optionId, ccfType) => {
    const normalizedCcfType = String(ccfType).toLowerCase().trim();
    return filteredAssessments.some(
      assessment => {
        const assessmentType = assessment.ccf_type ? String(assessment.ccf_type).toLowerCase().trim() : '';
        return assessment.ccf_option === optionId && assessmentType === normalizedCcfType;
      }
    );
  };


  const isNotApplicableSelected = (ccfType) => {
    return notApplicableState[ccfType] || false;
  };


  const findAssessmentByOptionAndType = (optionId, ccfType) => {
    return filteredAssessments.find(
      assessment => assessment.ccf_option === optionId && assessment.ccf_type === ccfType
    );
  };


  const scoresByType = useMemo(() => {
    const scores = {
      input: 0,
      logic: 0,
      output: 0,
    };

    filteredAssessments.forEach(assessment => {
      if (assessment.ccf_type && assessment.ccf_type !== CCF_TYPES.NOT_APPLICABLE && assessment.ccf_option) {
        const option = ccfOptions?.find(opt => opt.id === assessment.ccf_option);
        if (option && scores[assessment.ccf_type] !== undefined) {
          scores[assessment.ccf_type] += (option?.score || 0);
        }
      }
    });

    return scores;
  }, [filteredAssessments, ccfOptions]);

  const resultsByType = useMemo(() => {
    const resultFor = (ccfType) =>
      isNotApplicableSelected(ccfType) ? 'N/A' : getCcfResult(scoresByType[ccfType]);
    return {
      input: resultFor('input'),
      logic: resultFor('logic'),
      output: resultFor('output'),
    };
  }, [scoresByType, notApplicableState]);


  const overallScore = useMemo(() => {

    const requiredSectionsMissing = Object.keys(sectionRequirements).some(type => {
      const requirement = sectionRequirements[type];
      const hasAssessments = filteredAssessments.some(a => a.ccf_type === type);
      const isNA = isNotApplicableSelected(type);
      return requirement.required && !hasAssessments && !isNA;
    });


    if (requiredSectionsMissing) {
      return 0;
    }

    const applicableScores = [];

    Object.keys(scoresByType).forEach(ccfType => {
      const hasAssessments = filteredAssessments.some(
        assessment => assessment.ccf_type === ccfType
      );
      const isNA = isNotApplicableSelected(ccfType);
      if (hasAssessments && !isNA) {
        applicableScores.push(scoresByType[ccfType]);
      }
    });

    return applicableScores.length > 0 ? Math.min(...applicableScores) : 0;
  }, [scoresByType, filteredAssessments, sectionRequirements, notApplicableState]);

  const overallResult = useMemo(() => {

    const requiredSectionsMissing = Object.keys(sectionRequirements).some(type => {
      const requirement = sectionRequirements[type];
      const hasAssessments = filteredAssessments.some(a => a.ccf_type === type);
      const isNA = isNotApplicableSelected(type);
      return requirement.required && !hasAssessments && !isNA;
    });


    if (requiredSectionsMissing) {
      return 'FAILED';
    }

    return getCcfResult(overallScore);
  }, [overallScore, sectionRequirements, filteredAssessments, notApplicableState]);


  const handleCheckBoxChange = async (e, optionId, ccfType) => {
    const checked = e.target.checked;

    if (checked) {

      setUserSetNotApplicable(prev => ({
        ...prev,
        [ccfType]: false
      }));


      setUserUncheckedNotApplicable(prev => ({
        ...prev,
        [ccfType]: false
      }));


      setNotApplicableState(prev => ({
        ...prev,
        [ccfType]: false
      }));
      await createCCF([{ccf_option: optionId, ccf_type: ccfType}]);
    } else {
      const existingAssessment = findAssessmentByOptionAndType(optionId, ccfType);
      if (existingAssessment) {

        setDeletingAssessmentIds(prev => new Set(prev).add(existingAssessment.id));
        try {
          await deleteCCF(existingAssessment.id);
        } finally {

          setDeletingAssessmentIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(existingAssessment.id);
            return newSet;
          });
        }

      }
    }
  };

  // Handle "Not Applicable" checkbox change
  const handleNotApplicableChange = async (e, ccfType) => {
    const checked = e.target.checked;
    const field = `ccf_${String(ccfType).toLowerCase()}_not_applicable`;

    if (checked) {
      if (upsertAssessment) {
        await upsertAssessment({ [field]: true });
      }

      // Clear the "unchecked" flag since user is checking it
      setUserUncheckedNotApplicable(prev => ({
        ...prev,
        [ccfType]: false
      }));

      // Mark as user-initiated
      setUserSetNotApplicable(prev => ({
        ...prev,
        [ccfType]: true
      }));


      setNotApplicableState(prev => ({
        ...prev,
        [ccfType]: true
      }));


      const normalizedCcfType = String(ccfType).toLowerCase().trim();
      
      const allAssessmentsForType = ccfAssessments.filter(
        assessment => {
          if (!assessment || !assessment.id) return false;
          const assessmentType = assessment.ccf_type ? String(assessment.ccf_type).toLowerCase().trim() : '';
          return assessmentType === normalizedCcfType;
        }
      );
      
      const existingAssessments = allAssessmentsForType;
      
      if (existingAssessments.length === 0) {
        return;
      }

      const assessmentIdsToDelete = existingAssessments
        .map(a => a.id)
        .filter(id => id != null && id !== undefined);
      
      if (assessmentIdsToDelete.length === 0) {
        return;
      }
      
      assessmentIdsToDelete.forEach(id => {
        pendingDeletionsRef.current.add(id);
      });
      
      setDeletingAssessmentIds(prev => {
        const newSet = new Set(prev);
        assessmentIdsToDelete.forEach(id => newSet.add(id));
        return newSet;
      });
      

      const deletePromises = existingAssessments.map(assessment => {
        if (!assessment.id) {
          return Promise.resolve(null);
        }
        return deleteCCF(assessment.id).catch(err => {
          return null; 
        });
      });
      

      await Promise.all(deletePromises);

    } else {
      if (upsertAssessment) {
        await upsertAssessment({ [field]: false });
      }

      setUserUncheckedNotApplicable(prev => ({
        ...prev,
        [ccfType]: true
      }));


      setUserSetNotApplicable(prev => ({
        ...prev,
        [ccfType]: false
      }));


      setNotApplicableState(prev => ({
        ...prev,
        [ccfType]: false
      }));
    }
  };


  const generateTableData = (ccfType) => {
    const tableHeaders = ['No.', 'Measure Against CCF', 'Score', 'Action'];
    const tableKeys = ['id', 'measure_against_ccf', 'score', 'action'];

    return ccfOptions?.map((option, index) =>
      tableKeys?.map(key => {
        const value = option[key];
        const score = option['score'];

        if (key === 'action' && score > 0) {
          return (
            <input
              onChange={e => handleCheckBoxChange(e, option.id, ccfType)}
              checked={isOptionSelected(option.id, ccfType)}
              type="checkbox"
              disabled={isCreating || isDeleting || isNotApplicableSelected(ccfType)}
            />
          );
        } else if (key === 'id') {
          return index + 1;
        } else if (key === 'score') {
          return score > 0 ? value : '';
        } else if (key === 'measure_against_ccf') {
          return (
            <div style={{display: 'flex',flexDirection: 'column', gap: '0.5rem',maxWidth: '800px'}}>
              {' '}
              <span style={{fontWeight: 'bold'}}>{option['measure_against_ccf']}</span>{' '}
              {option['description'] && <p style={{fontSize: '14px', color: '#666',textWrap: 'wrap',margin: '0'}}>{option['description']}</p>}
            
            </div>
          );
        } else {
          return value;
        }
      }),
    );
  };

  // Render a CCF section (Input, Logic, or Output)
  const renderCCFSection = (ccfType, sectionName) => {
    const requirement = sectionRequirements[ccfType];
    const score = scoresByType[ccfType];
    const result = resultsByType[ccfType];
    const isNA = isNotApplicableSelected(ccfType);
    // Check if there are actually assessments for this type (using filtered assessments)
    const hasAssessments = filteredAssessments.some(a => a.ccf_type === ccfType);
    
    // Check if required section is missing data (required but no assessments and not marked as N/A)
    const isRequiredAndMissing = requirement.required && !hasAssessments && !isNA;

    if (!requirement.show) {
      return null;
    }

    return (
      <div
        key={ccfType}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          border: isRequiredAndMissing ? '1px solid #f44336' : '0.5px solid gray',
          padding: '1rem',
          borderRadius: '7px',
          width: '100%',
          marginBottom: '1rem',
          backgroundColor: 'transparent',
        }}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
            <h3 style={{fontSize: '18px', fontWeight: 'bold', margin: 0}}>
              CCF ({sectionName})
            </h3>
            {isRequiredAndMissing && (
              <span style={{
                padding: '0.25rem 0.75rem',
                backgroundColor: '#f44336',
                color: 'white',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                textTransform: 'uppercase'
              }}>
                ⚠️ Missing Data
              </span>
            )}
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
            <label style={{display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '14px'}}>
              <input
                type="checkbox"
                checked={isNA}
                onChange={e => handleNotApplicableChange(e, ccfType)}
                disabled={isCreating || isDeleting}
                title="Mark this section as not applicable"
              />
              Not Applicable
            </label>
          </div>
        </div>

        {/* Show subtle warning message for required sections missing data */}
        {isRequiredAndMissing && (
          <>
            <div
              style={{
                padding: '0.5rem 0.75rem',
                backgroundColor: '#fff3e0',
                borderRadius: '4px',
                border: '1px solid #ff9800',
                marginBottom: '0.5rem',
              }}>
              <span style={{
                fontSize: '14px',
                color: '#e65100'
              }}>
                This section needs data. Please select CCF options above or mark as Not Applicable.
              </span>
            </div>
          </>
        )}

        {!isNA && (
          <>
            <EditableTable 
              headers={['No.', 'Measure Against CCF', 'Score', 'Action']} 
              data={generateTableData(ccfType)} 
            />
            
            {(score > 0 || result !== 'N/A') && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  padding: '0.75rem',
                  backgroundColor:
                    result === 'PASS'
                      ? '#e8f5e9'
                      : result === 'FAIL'
                      ? '#ffebee'
                      : '#f5f5f5',
                  borderRadius: '5px',
                  border: `1px solid ${
                    result === 'PASS'
                      ? '#4caf50'
                      : result === 'FAIL'
                      ? '#f44336'
                      : '#9e9e9e'
                  }`,
                  marginTop: '0.5rem',
                }}>
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color:
                      result === 'PASS'
                        ? '#2e7d32'
                        : result === 'FAIL'
                        ? '#c62828'
                        : '#616161',
                  }}>
                  CCF ({sectionName}) Score: {score}
                </span>
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color:
                      result === 'PASS'
                        ? '#2e7d32'
                        : result === 'FAIL'
                        ? '#c62828'
                        : '#616161',
                  }}>
                  CCF ({sectionName}) Result: {result}
                </span>
              </div>
            )}
          </>
        )}

        {isNA && (
          <div
            style={{
              padding: '0.75rem',
              backgroundColor: '#f5f5f5',
              borderRadius: '5px',
              border: '1px solid #9e9e9e',
              color: '#616161',
              fontStyle: 'italic',
            }}>
            This section will not be applicable for CCF score calculation.
          </div>
        )}
      </div>
    );
  };

  return (
    <CustomModal title="CCF" show={show} setShow={setShow}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          justifyItems: 'start',
          textAlign: 'start',
          fontSize: '18px',
          marginBottom: '16px',
        }}>
        <span>Common Cause Failures Assessment</span>
        <span>Control System Category: Category {category}</span>

        {/* Overall CCF Score and Result at Top */}
        {(overallScore > 0 || overallResult === 'FAILED' || filteredAssessments.length > 0) && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              padding: '0.75rem',
              backgroundColor:
                overallResult === 'PASS'
                  ? '#e8f5e9'
                  : overallResult === 'FAIL' || overallResult === 'FAILED'
                  ? '#ffebee'
                  : '#f5f5f5',
              borderRadius: '5px',
              border: `1px solid ${
                overallResult === 'PASS'
                  ? '#4caf50'
                  : overallResult === 'FAIL' || overallResult === 'FAILED'
                  ? '#f44336'
                  : '#9e9e9e'
              }`,
              marginTop: '0.5rem',
            }}>
            <span
              style={{
                fontWeight: 'bold',
                fontSize: '18px',
                color:
                  overallResult === 'PASS'
                    ? '#2e7d32'
                    : overallResult === 'FAIL' || overallResult === 'FAILED'
                    ? '#c62828'
                    : '#616161',
              }}>
              Overall CCF Score: {overallScore}
            </span>
            <span
              style={{
                fontWeight: 'bold',
                fontSize: '18px',
                color:
                  overallResult === 'PASS'
                    ? '#2e7d32'
                    : overallResult === 'FAIL' || overallResult === 'FAILED'
                    ? '#c62828'
                    : '#616161',
              }}>
              Overall CCF Result: {overallResult}
            </span>
            <span style={{fontSize: '14px', color: '#666', fontStyle: 'italic', marginTop: '4px'}}>
              (Based on worst case across all applicable sections)
            </span>
          </div>
        )}
      </div>

      <Box sx={{ width: '100%', mt: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          aria-label="CCF assessment tabs"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="CCF (Input)" />
          <Tab label="CCF (Logic)" />
          <Tab label="CCF (Output)" />
        </Tabs>

        <Box role="tabpanel" hidden={activeTab !== 0}>
          {activeTab === 0 && (
            <Box sx={{ pt: 2 }}>
              {renderCCFSection(CCF_TYPES.INPUT, 'Input')}
            </Box>
          )}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== 1}>
          {activeTab === 1 && (
            <Box sx={{ pt: 2 }}>
              {renderCCFSection(CCF_TYPES.LOGIC, 'Logic')}
            </Box>
          )}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== 2}>
          {activeTab === 2 && (
            <Box sx={{ pt: 2 }}>
              {renderCCFSection(CCF_TYPES.OUTPUT, 'Output')}
            </Box>
          )}
        </Box>

        {(isCreating || isDeleting) && (
          <div
            className="text-center"
            style={{fontSize: '14px', color: '#666', marginTop: '1rem'}}>
            {isCreating ? 'Saving...' : 'Deleting...'}
          </div>
        )}
      </Box>
    </CustomModal>
  );
};

export default CCF;
